import { utils, write } from '@e965/xlsx';
import { triggerDownload } from '@/utils/pdfHelpers';

/**
 * Supported output formats for reports.
 *
 * - `html`  – Opens the rendered HTML in a new browser tab (current default).
 * - `excel` – Parses HTML tables and downloads an .xlsx workbook.
 * - `pdf`   – Opens the HTML in a new tab and triggers the browser print dialog.
 */
export type ReportOutputFormat = 'html' | 'excel' | 'pdf';

/**
 * Reserved parameter key injected by `ReportsPage` so runners can read the
 * desired output format from the standard params record.
 */
export const OUTPUT_FORMAT_KEY = '__outputFormat';

/** Read the output format from run params, defaulting to 'html'. */
export const getOutputFormat = (params: Record<string, unknown>): ReportOutputFormat => {
  const raw = params[OUTPUT_FORMAT_KEY];
  if (raw === 'excel' || raw === 'pdf') return raw;
  return 'html';
};

// ---------------------------------------------------------------------------
// Excel helpers
// ---------------------------------------------------------------------------

/** Characters invalid in Excel sheet tab names. */
const INVALID_SHEET_CHARS = /[:\\/?*[\]]/g;

/** Sanitize a candidate sheet name for Excel compatibility (max 31 chars). */
const sanitizeSheetName = (name: string, usedNames: Set<string>): string => {
  let sanitized = name.replace(INVALID_SHEET_CHARS, '').trim().slice(0, 31);
  if (!sanitized) sanitized = 'Sheet';

  let uniqueName = sanitized;
  let counter = 2;
  while (usedNames.has(uniqueName)) {
    const suffix = ` (${counter})`;
    uniqueName = sanitized.slice(0, 31 - suffix.length) + suffix;
    counter++;
  }
  usedNames.add(uniqueName);
  return uniqueName;
};

/** Extract all `<table>` elements from an HTML string and convert to XLSX. */
const htmlToXlsx = (html: string, baseFilename: string): void => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const tables = doc.querySelectorAll('table');

  const wb = utils.book_new();

  if (tables.length === 0) {
    // Fallback: create a single sheet with all visible text
    const ws = utils.aoa_to_sheet([['No tabular data found in report']]);
    utils.book_append_sheet(wb, ws, 'Report');
  } else {
    const usedNames = new Set<string>();
    tables.forEach((table, index) => {
      const ws = utils.table_to_sheet(table);
      const rawName = table.getAttribute('data-sheet-name');
      let sheetName: string;
      if (rawName) {
        sheetName = sanitizeSheetName(rawName, usedNames);
      } else {
        sheetName = tables.length === 1 ? 'Report' : `Sheet${String(index + 1)}`;
        usedNames.add(sheetName);
      }
      utils.book_append_sheet(wb, ws, sheetName);
    });
  }

  const buffer = write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const filename = baseFilename.replace(/\.html$/i, '') + '.xlsx';
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

// ---------------------------------------------------------------------------
// PDF helpers
// ---------------------------------------------------------------------------

/** Inject a small auto-print script into the HTML and open in a new browser tab. */
const htmlToPdfPrint = (html: string): void => {
  // Inject a script right before </body> that fires window.print() after load.
  const printScript = `<script>window.addEventListener('load',function(){window.print();});</script>`;
  let enriched: string;
  if (html.includes('</body>')) {
    enriched = html.replace('</body>', `${printScript}</body>`);
  } else {
    enriched = html + printScript;
  }

  const blob = new Blob([enriched], { type: 'text/html;charset=utf-8' });
  triggerDownload(blob, 'report.html');
};

// ---------------------------------------------------------------------------
// Main entry point
// ---------------------------------------------------------------------------

/**
 * Route a generated HTML report to the appropriate output channel based on
 * the requested format.
 *
 * Runners should call this instead of `triggerDownload` directly.
 */
export const outputReport = (
  html: string,
  filename: string,
  format: ReportOutputFormat = 'html',
): void => {
  switch (format) {
    case 'excel':
      htmlToXlsx(html, filename);
      break;
    case 'pdf':
      htmlToPdfPrint(html);
      break;
    case 'html':
    default: {
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      triggerDownload(blob, filename);
      break;
    }
  }
};
